/**
 * Word Pyramids — chargement des dictionnaires de puzzles.
 *
 * Format : 1 fichier JSON par langue dans `apps/web/public/dictionaries/`,
 * importé en build-time par Vite (inlined dans le bundle, pas de fetch
 * runtime — taille totale ~6 KB pour FR+EN, négligeable). Les fichiers
 * publics restent disponibles pour debugging et caching CDN ultérieur.
 *
 * Choix dictionary (cf STATUS-wave-newgames-n2.md) : Option curated puzzle
 * templates avec accepted-words baked-in. Pas de gros lexique français
 * (Lexique.org = 60 MB), pas de package npm (`wordlist-iso` ajoute du
 * poids et augmente la surface bundle). 40 puzzles FR + 20 puzzles EN
 * suffisent pour 50 niveaux + variabilité (rotation déterministe par
 * trialIdx + lang + level).
 */
import puzzlesFrRaw from '../../../public/dictionaries/word-pyramids-fr.json';
import puzzlesEnRaw from '../../../public/dictionaries/word-pyramids-en.json';
const DICTIONARIES = {
    fr: puzzlesFrRaw,
    en: puzzlesEnRaw,
};
export function getDictionary(lang) {
    return DICTIONARIES[lang];
}
/**
 * Sélectionne un puzzle de longueur >= letterCount. Si plusieurs candidats,
 * choix déterministe via `seed` (trialIdx). Si aucun candidat exact, retourne
 * celui dont la longueur est la plus proche supérieure ou égale ; sinon, le
 * dernier puzzle de la liste (longueur max disponible).
 */
export function pickPuzzle(lang, letterCount, seed) {
    const dict = getDictionary(lang);
    const candidates = dict.puzzles.filter((p) => p.letters.length === letterCount);
    if (candidates.length > 0) {
        const idx = ((seed >>> 0) % candidates.length) | 0;
        return candidates[idx];
    }
    // Fallback : longueur la plus proche supérieure ou égale.
    const above = dict.puzzles
        .filter((p) => p.letters.length >= letterCount)
        .sort((a, b) => a.letters.length - b.letters.length);
    if (above.length > 0) {
        const idx = ((seed >>> 0) % above.length) | 0;
        return above[idx];
    }
    // Aucun puzzle >= letterCount : retourne le plus long disponible.
    const sorted = [...dict.puzzles].sort((a, b) => b.letters.length - a.letters.length);
    return sorted[0];
}
/**
 * Normalise un mot user pour validation : MAJUSCULE + suppression accents
 * basiques (assez pour vocabulaire FR commun). N'utilise pas Intl.Collator
 * pour rester léger et déterministe.
 */
export function normalizeWord(input) {
    return input
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z]/g, '');
}
/**
 * Vérifie qu'un mot est composable depuis le pool letters (chaque lettre
 * du pool ne peut être utilisée qu'une seule fois par mot).
 */
export function isComposable(word, pool) {
    const counts = new Map();
    for (const c of pool)
        counts.set(c, (counts.get(c) ?? 0) + 1);
    for (const c of word) {
        const left = counts.get(c) ?? 0;
        if (left === 0)
            return false;
        counts.set(c, left - 1);
    }
    return true;
}
export function isAccepted(word, puzzle) {
    if (!isComposable(word, puzzle.letters))
        return false;
    return puzzle.accepted.includes(word);
}
//# sourceMappingURL=data.js.map